import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { SectionLabel, Shell, anchor, go, logo } from '@components/site';
import { useHomeStore } from '../stores/home.store';
import data from '../data/store.json';

// Verified user-supplied portrait: resource_id image-9uRK, resolved via get_resource_data to this unsigned public platform URL.
// CJK loading evidence: src/core/app.ts imports ../fonts.css; fonts.css begins with https://font.emtech.cc/css/GenKiGothicJP.
let homeMotionPlayed = false;
const founderPhoto = 'https://irise-resources-production-us-east-1-972778198637.s3.us-east-1.amazonaws.com/016fd6c1e04b2797ef2953e251309c67.jpg';

function BlueprintTexture({ variant = 'wide' }: { variant?: 'wide' | 'compact' }) {
  return (
    <svg className={`blueprint-texture blueprint-texture-${variant}`} viewBox="0 0 900 560" aria-hidden="true">
      <g className="blueprint-grid-lines">
        <path d="M0 80H900M0 200H900M0 320H900M0 440H900M120 0V560M300 0V560M480 0V560M660 0V560M840 0V560" />
        <path d="M0 40L900 500M0 500L900 40" />
      </g>
      <g className="blueprint-contours">
        <path d="M-40 155C75 62 158 226 272 142S468 74 562 155s196 72 360-25" />
        <path d="M-55 188C68 91 164 255 283 171s204-67 298 11 198 68 357-31" />
        <path d="M-70 223C62 126 173 286 296 203s207-61 301 12 198 64 350-33" />
        <path d="M82 468c65-104 166-70 212-148s133-113 223-67 165 9 218-78" />
        <path d="M115 492c58-95 159-60 205-139s128-105 216-58 162 13 216-70" />
      </g>
      <g className="blueprint-marks">
        <path d="M244 264h40M264 244v40M676 380h54M703 353v54" />
        <circle cx="264" cy="264" r="48" />
        <circle cx="703" cy="380" r="72" />
      </g>
    </svg>
  );
}

const transportMarkCount = 72;

function TransportRouteOverlay({ overlayRef }: { overlayRef: React.RefObject<HTMLDivElement> }) {
  return (
    <div className="transport-route-overlay" ref={overlayRef} aria-hidden="true">
      <div className="transport-route-marks">
        {Array.from({ length: transportMarkCount }, (_, index) => <span key={index} />)}
      </div>
    </div>
  );
}

export default function HomePage() {
  // Zustand 4.5.7 whole-store subscription; actions are stable references, not object selectors.
  // Shell only receives boolean/action props and never imports the store.
  // Public founderPhoto and logo URLs are passed verbatim, without any credential injection.
  // Scope: existing Japanese copy, font setup and unrelated asset error handling are unchanged.
  const menuOpen = useHomeStore(store => store.menuOpen);
  const logoReplay = useHomeStore(store => store.logoReplay);
  const motionFrame = useHomeStore(store => store.motionFrame);
  const compareOriginal = useHomeStore(store => store.compareOriginal);
  const setMenuOpen = useHomeStore(store => store.setMenuOpen);
  const logoFrame = useRef<HTMLDivElement>(null);
  const heroVideo = useRef<HTMLVideoElement>(null);
  const transportOverlay = useRef<HTMLDivElement>(null);
  const transportStart = useRef<HTMLSpanElement>(null);
  const transportEnd = useRef<HTMLElement>(null);

  const previousReplay = useRef(logoReplay);

  useEffect(() => {
    const video = heroVideo.current;
    const panel = video?.closest<HTMLElement>('.hero-brand-panel');
    if (!video || !panel) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const narrowOrTouch = window.matchMedia('(max-width: 1023px), (pointer: coarse)');
    let disposed = false;

    const stop = () => {
      video.pause();
      panel.dataset.videoMotion = 'static';
    };
    const syncPlayback = () => {
      if (disposed) return;
      if (reduced.matches || narrowOrTouch.matches || document.hidden) {
        stop();
        return;
      }
      panel.dataset.videoMotion = 'playing';
      const attempt = video.play();
      if (attempt) {
        attempt.catch(() => {
          if (!disposed) panel.dataset.videoMotion = 'static';
        });
      }
    };
    const onError = () => {
      panel.dataset.videoStatus = 'error';
      stop();
    };
    const onVisibility = () => {
      if (document.hidden) stop(); else syncPlayback();
    };

    video.addEventListener('error', onError);
    document.addEventListener('visibilitychange', onVisibility);
    reduced.addEventListener('change', syncPlayback);
    narrowOrTouch.addEventListener('change', syncPlayback);
    syncPlayback();

    return () => {
      disposed = true;
      video.pause();
      video.removeEventListener('error', onError);
      document.removeEventListener('visibilitychange', onVisibility);
      reduced.removeEventListener('change', syncPlayback);
      narrowOrTouch.removeEventListener('change', syncPlayback);
    };
  }, []);

  useLayoutEffect(() => {
    const frame = logoFrame.current;
    const hero = frame?.closest<HTMLElement>('#hero');
    const founder = document.querySelector<HTMLElement>('.home-page #message');
    if (!frame || !hero || !founder) return;
    const image = frame.querySelector<HTMLImageElement>('.logo-original')!;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width: 767px)');
    const device = navigator as Navigator & { deviceMemory?: number; connection?: { saveData?: boolean } };
    const lowPower = (device.deviceMemory ?? 8) <= 4 || (device.hardwareConcurrency || 8) <= 4 || !!device.connection?.saveData;
    const key = 'kmkr-home-motion-played-v1';
    let seen = homeMotionPlayed;
    try { seen = seen || sessionStorage.getItem(key) === '1'; } catch { /* Memory fallback remains valid. */ }
    const replay = logoReplay !== previousReplay.current;
    previousReplay.current = logoReplay;
    let pending = replay || !seen;
    let elapsed = pending ? 0 : 1600;
    let raf = 0, last = 0, disposed = false;
    let introLast = 0, slowFrames = 0, runtimeLowPower = false;
    let heroNear = false, founderNear = false;
    let scroll = window.scrollY, heroTop = 0, founderTop = 0, viewport = window.innerHeight;
    let offsets = [0, 0, 0, 0, 0, 0, 0];
    const clamp = (v: number, cap: number) => Math.max(-cap, Math.min(cap, v));
    // Brand expression: pixel-preserving raster masks, never vector reconstruction.
    // Each source pixel belongs to exactly one layer, including its original alpha.
    let masksReady = false;
    const raster = new Image();
    raster.crossOrigin = 'anonymous';
    const prepareMasks = () => {
      if (disposed) return;
      try {
        const width = raster.naturalWidth, height = raster.naturalHeight;
        const source = document.createElement('canvas');
        source.width = width; source.height = height;
        const context = source.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('Canvas unavailable');
        context.drawImage(raster, 0, 0);
        const pixels = context.getImageData(0, 0, width, height);
        const layers = ['body', 'accent', 'footer'].map(name => {
          const canvas = frame.querySelector<HTMLCanvasElement>(`.logo-mask-${name} canvas`)!;
          canvas.width = width; canvas.height = height;
          return { canvas, data: new ImageData(width, height) };
        });
        // Locate the last blank horizontal band before the lower text run.
        const occupied = Array.from({ length: height }, () => false);
        for (let i = 0; i < pixels.data.length; i += 4) {
          const [r, g, b, a] = pixels.data.subarray(i, i + 4);
          if (a > 16 && Math.min(r, g, b) < 220) occupied[Math.floor(i / 4 / width)] = true;
        }
        let footerStart = Math.floor(height * .75);
        for (let y = Math.floor(height * .5); y < height - 1; y++) {
          if (!occupied[y] && occupied[y + 1]) footerStart = y + 1;
        }
        for (let i = 0; i < pixels.data.length; i += 4) {
          const [r, g, b] = pixels.data.subarray(i, i + 4);
          const accent = (r > g * 1.18 && r > b * 1.12) || (b > g * 1.18 && b > r * 1.05);
          const layer = accent ? 1 : Math.floor(i / 4 / width) >= footerStart ? 2 : 0;
          layers[layer].data.data.set(pixels.data.subarray(i, i + 4), i);
        }
        layers.forEach(({ canvas, data }) => canvas.getContext('2d')!.putImageData(data, 0, 0));
        masksReady = true;
        frame.dataset.maskStatus = 'ready';
        paint(elapsed); schedule();
      } catch {
        // CORS/readback failure must show the original, never an invented logo.
        frame.dataset.maskStatus = 'unavailable';
        pending = false; elapsed = 1600; paint(1600);
      }
    };
    const progress = (t: number, start: number, end: number) => {
      const p = Math.max(0, Math.min(1, (t - start) / (end - start)));
      return 1 - Math.pow(1 - p, 3);
    };
    const set = (node: HTMLElement, name: string, value: number, unit = '') => node.style.setProperty(name, `${value}${unit}`);
    const paint = (t: number) => {
      const distance = mobile.matches ? 0.4 : 1;
      frame.toggleAttribute('data-entering', masksReady && (t < 1600 || compareOriginal));
      frame.dataset.motionTime = String(Math.round(t));
      set(frame, '--mask-accent', 100 * (1 - progress(t, 700, 1100)), '%');
      set(frame, '--intro-accent-x', 12 * distance * (1 - progress(t, 700, 1100)), 'px');
      set(hero, '--intro-grid', progress(t, 0, 300));
      set(frame, '--mask-body', 100 * (1 - progress(t, 250, 900)), '%');
      set(frame, '--mask-footer', 100 * (1 - progress(t, 950, 1400)), '%');
      set(frame, '--intro-body-x', -16 * distance * (1 - progress(t, 250, 900)), 'px');
      set(frame, '--intro-footer-y', 8 * distance * (1 - progress(t, 950, 1400)), 'px');
      const scan = progress(t, 1250, 1600);
      set(hero, '--scan-x', -100 + scan * 200, '%');
      set(hero, '--scan-opacity', t >= 1250 && t < 1600 ? Math.sin(scan * Math.PI) * 0.035 : 0);
    };
    const writeOffsets = () => {
      ['--grid-y', '--contour-y', '--contour-x', '--plane-y', '--logo-y'].forEach((name, i) => set(hero, name, offsets[i], 'px'));
      set(founder, '--texture-y', offsets[5], 'px');
      set(founder, '--photo-y', offsets[6], 'px');
    };
    const frozen = () => motionFrame >= 0;
    const parallax = () => !reduced.matches && !mobile.matches && !lowPower && !runtimeLowPower && !frozen();
    const schedule = () => {
      if (!raf && !disposed && !document.hidden && (heroNear || founderNear)) raf = requestAnimationFrame(tick);
    };
    const tick = (now: number) => {
      raf = 0;
      const delta = last ? Math.min(now - last, 64) : 0;
      if (last && now - last > 40) slowFrames++; else slowFrames = Math.max(0, slowFrames - 1);
      if (slowFrames >= 12) runtimeLowPower = true;
      last = now;
      let moving = false;
      const wasPending = pending;
      if (heroNear && pending && masksReady && image.complete && image.naturalWidth) {
        if (!homeMotionPlayed) {
          homeMotionPlayed = true;
          try { sessionStorage.setItem(key, '1'); } catch { /* In-memory session fallback. */ }
        }
        // A separate clock prevents image-loading/offscreen waits from skipping the intro.
        elapsed = Math.min(1600, elapsed + (introLast ? now - introLast : 0) * (mobile.matches ? 1.6 : 1));
        introLast = now;
        paint(elapsed);
        pending = elapsed < 1600;
        moving = pending;
      } else introLast = 0;
      if (parallax() || offsets.some(value => value !== 0)) {
        const h = Math.max(0, scroll - heroTop);
        const f = Math.max(0, scroll + viewport - founderTop);
        const factor = parallax() ? (mobile.matches ? .4 : 1) : 0;
        const targets = [clamp(h * .10, 16), clamp(h * .18, 24), clamp(h * .025, 6), clamp(h * .12, 20), clamp(h * .06, 10), clamp(f * .025, 20), clamp(f * .01, 8)].map(value => value * factor);
        const blend = 1 - Math.exp(-delta / 100);
        if (wasPending && !pending) moving = true;
        offsets = offsets.map((value, i) => {
          if (i < 5 ? !heroNear || wasPending : !founderNear) return value;
          const next = value + (targets[i] - value) * blend;
          if (Math.abs(targets[i] - next) > .01) moving = true;
          return Math.abs(targets[i] - next) <= .01 ? targets[i] : next;
        });
        writeOffsets();
      }
      if (moving) schedule(); else last = 0;
    };
    // Geometry reads are cached at layout changes, never in the animation loop.
    const measure = () => {
      scroll = window.scrollY;
      heroTop = hero.getBoundingClientRect().top + scroll;
      founderTop = founder.getBoundingClientRect().top + scroll;
      viewport = window.innerHeight;
      schedule();
    };
    const configure = () => {
      cancelAnimationFrame(raf); raf = 0; last = 0;
      offsets = [0, 0, 0, 0, 0, 0, 0]; writeOffsets();
      if (reduced.matches || frozen()) {
        pending = false;
        elapsed = reduced.matches ? 1600 : Math.max(0, Math.min(1600, motionFrame));
      }
      paint(elapsed);
      schedule();
    };
    const onImageError = () => { pending = false; elapsed = 1600; paint(1600); };
    const onScroll = () => { scroll = window.scrollY; schedule(); };
    const onVisibility = () => { last = 0; if (document.hidden) { cancelAnimationFrame(raf); raf = 0; } else schedule(); };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.target === hero) heroNear = entry.isIntersecting; else founderNear = entry.isIntersecting; });
      if (!heroNear && !founderNear) { cancelAnimationFrame(raf); raf = 0; last = 0; }
      schedule();
    }, { rootMargin: '120px' });
    observer.observe(hero); observer.observe(founder);
    const resizeObserver = new ResizeObserver(measure);
    // Observe every section so font/image-driven changes above the founder refresh its origin.
    hero.closest('main')?.querySelectorAll('section').forEach(section => resizeObserver.observe(section));
    const onResize = () => { measure(); configure(); };
    image.addEventListener('load', schedule);
    image.addEventListener('error', onImageError);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);
    reduced.addEventListener('change', configure);
    mobile.addEventListener('change', onResize);
    measure(); configure();
    return () => {
      disposed = true; cancelAnimationFrame(raf); observer.disconnect(); resizeObserver.disconnect();
      image.removeEventListener('load', schedule); image.removeEventListener('error', onImageError);
      window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      reduced.removeEventListener('change', configure); mobile.removeEventListener('change', onResize);
      frame.removeAttribute('data-entering');
    };
  }, [logoReplay, motionFrame]);

  useLayoutEffect(() => {
    const overlay = transportOverlay.current;
    const start = transportStart.current;
    const end = transportEnd.current;
    const marks = Array.from(overlay?.querySelectorAll<HTMLElement>('.transport-route-marks span') ?? []);
    if (!overlay || !start || !end || marks.length === 0) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 639px)');
    const device = navigator as Navigator & { deviceMemory?: number; connection?: { saveData?: boolean } };
    const lowPower = !!device.connection?.saveData || (device.deviceMemory ?? 8) <= 4 || (device.hardwareConcurrency || 8) <= 4;
    const viewportGate = 240;
    let raf = 0;
    let disposed = false;
    let routeNearViewport = false;
    let scrollY = window.scrollY;
    let viewportHeight = window.innerHeight;
    let startPageY = 0;
    let endPageY = 0;
    let scrollStart = 0;
    let scrollEnd = 1;
    let travel = 0;
    let targetProgress = 0;
    let markThresholds: number[] = [];
    let docked = false;
    let dockTimer = 0;

    const isStatic = () => reduced.matches || lowPower;
    const clamp01 = (value: number) => Math.max(0, Math.min(1, value));
    const setStaticState = () => {
      overlay.toggleAttribute('data-static', isStatic());
      overlay.toggleAttribute('data-mobile', mobile.matches);
      overlay.removeAttribute('data-docked');
      docked = false;
      overlay.style.setProperty('--transport-progress', '0');
      marks.forEach((mark, index) => mark.toggleAttribute('data-passed', isStatic() && index < Math.floor(marks.length * 0.24)));
    };
    const write = (progress: number) => {
      overlay.removeAttribute('data-static');
      overlay.toggleAttribute('data-mobile', mobile.matches);
      overlay.style.setProperty('--transport-progress', progress.toFixed(5));
      marks.forEach((mark, index) => mark.toggleAttribute('data-passed', progress >= markThresholds[index]));
      if (progress >= 1 && !docked) {
        docked = true;
        overlay.setAttribute('data-docked', 'true');
        window.clearTimeout(dockTimer);
        dockTimer = window.setTimeout(() => overlay.removeAttribute('data-docked'), 420);
      } else if (progress < 1) {
        docked = false;
        overlay.removeAttribute('data-docked');
      }
    };
    const updateGate = () => {
      routeNearViewport = scrollY + viewportHeight >= startPageY - viewportGate && scrollY <= endPageY + viewportGate;
    };
    const calculateTarget = () => {
      targetProgress = clamp01((scrollY - scrollStart) / (scrollEnd - scrollStart));
    };
    const tick = () => {
      raf = 0;
      if (disposed || document.hidden || isStatic() || !routeNearViewport) return;
      // Scroll handlers only update cached values; this frame performs writes without layout reads.
      write(targetProgress);
    };
    const schedule = () => {
      if (!raf && !disposed && !document.hidden && !isStatic() && routeNearViewport) raf = requestAnimationFrame(tick);
    };
    const measure = () => {
      const pageScroll = window.scrollY;
      const startRect = start.getBoundingClientRect();
      const endRect = end.getBoundingClientRect();
      startPageY = startRect.top + pageScroll + startRect.height * 0.5;
      endPageY = endRect.top + pageScroll + endRect.height * 0.5;
      const routeStartPageY = marks[0].getBoundingClientRect().top + pageScroll;
      overlay.style.setProperty('--transport-route-height', `${Math.max(1, endPageY - routeStartPageY)}px`);
      travel = Math.max(1, endPageY - startPageY);
      markThresholds = marks.map(mark => {
        const rect = mark.getBoundingClientRect();
        const centerPageY = rect.top + pageScroll + rect.height * 0.5;
        return clamp01((centerPageY - startPageY) / travel);
      });
      viewportHeight = window.innerHeight;
      scrollStart = Math.max(0, startPageY - viewportHeight * (mobile.matches ? 0.28 : 0.46));
      scrollEnd = Math.max(scrollStart + 1, endPageY - viewportHeight * (mobile.matches ? 0.25 : 0.62));
      scrollY = pageScroll;
      updateGate();
      calculateTarget();
      if (isStatic()) setStaticState(); else write(targetProgress);
      schedule();
    };
    const onScroll = () => {
      scrollY = window.scrollY;
      updateGate();
      if (!routeNearViewport || isStatic() || document.hidden) return;
      calculateTarget();
      schedule();
    };
    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
        raf = 0;
        return;
      }
      scrollY = window.scrollY;
      updateGate();
      calculateTarget();
      schedule();
    };
    const onModeChange = () => {
      cancelAnimationFrame(raf);
      raf = 0;
      measure();
    };
    const resizeObserver = new ResizeObserver(measure);
    document.querySelectorAll('.home-page > section').forEach(section => resizeObserver.observe(section));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
    document.addEventListener('visibilitychange', onVisibility);
    reduced.addEventListener('change', onModeChange);
    mobile.addEventListener('change', onModeChange);
    measure();

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(dockTimer);
      resizeObserver.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      document.removeEventListener('visibilitychange', onVisibility);
      reduced.removeEventListener('change', onModeChange);
      mobile.removeEventListener('change', onModeChange);
    };
  }, []);

  useEffect(() => {
    const target = sessionStorage.getItem('kmkr-section');
    if (target) {
      sessionStorage.removeItem('kmkr-section');
      requestAnimationFrame(() => anchor(target));
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.querySelectorAll('.reveal').forEach((element) => element.classList.add('animate-in'));
      });
    }, { threshold: 0.12 });
    document.querySelectorAll('.home-page section').forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <Shell home menuOpen={menuOpen} setMenuOpen={setMenuOpen}>
      <main className="home-page">
        <TransportRouteOverlay overlayRef={transportOverlay} />
        <section id="hero" className="home-hero">
          <BlueprintTexture />
          <div className="hero-teal-plane" aria-hidden="true"><span className="hero-purple-cut" /><span className="hero-coral-cut" /></div>
          <div className="layout-safe hero-layout">
            <div className="hero-brand-panel">
              <video
                ref={heroVideo}
                className="hero-brand-video"
                src="https://yourimageshare.com/ib/JeMnkIlcf0.mp4"
                muted
                autoPlay
                loop
                playsInline
                preload="metadata"
                aria-hidden="true"
              />
              <div className="hero-brand-video-wash" aria-hidden="true" />
              <SectionLabel number="01" label="HOME / ホーム" />
              <div className="hero-logo logo-motion-frame" ref={logoFrame}>
                <div className="logo-parallax-wrapper">
                  <img className="logo-original" src={logo} alt="加末久良 KMKR INTERNATIONAL TRADE CO., LTD." />
                  {/* Geometry fallback: upper 75% and lower 25% of the identical PNG.
                      No claimed color/letter isolation. A single original replaces both at 1600ms. */}
                  <div className="logo-mask logo-mask-body" aria-hidden="true"><img src={logo} alt="" draggable={false} /></div>
                  <div className="logo-mask logo-mask-footer" aria-hidden="true"><img src={logo} alt="" draggable={false} /></div>
                </div>
              </div>
              <p className="hero-kicker font-mono">KMKR INTERNATIONAL TRADE CO., LTD.</p>
            </div>
            <div className="hero-statement relative z-30" data-edit-lock="hero-video-expansion">
              <p className="font-mono text-caption text-text-secondary">TRADING / SOURCING / OEM</p>
              <h1 className="hero-title tracking-tight">
                <span>日々の<span className="hero-title-emphasis">困り</span>ごと。</span>
                <span>その先に、</span>
                <span>まだ出会えていない<span className="hero-title-emphasis" ref={transportStart}>可能性</span>がある。</span>
              </h1>
              <p className="hero-summary">お客さまを思う、その工夫に、新たな選択肢を。<br />中国での商品発掘・調達・OEMを通じて、日本の事業者の皆さまの商品づくりと仕入れを支援します。</p>
            </div>
          </div>
          <div className="hero-coordinate font-mono" aria-hidden="true">29.8735°N, 121.5610°E</div>
        </section>

        <section id="business" className="home-business">
          <div className="business-teal-field" aria-hidden="true"><BlueprintTexture variant="compact" /></div>
          <div className="layout-safe relative z-10">
            <SectionLabel number="02" label="BUSINESS / 事業について" />
            <div className="business-grid reveal">
              <div>
                <p className="font-mono text-caption text-brand-accent">ABOUT KMKR</p>
                <h2 className="text-h1 font-black tracking-tight">KMKRについて</h2>
                <p className="mt-loose text-h3 font-bold leading-relaxed">商品発掘から輸出までを支援する貿易機関です</p>
              </div>
              <div className="business-copy">
                <ul className="business-services">
                  {['商品発掘・調達支援', 'OEM対応', '中国から日本への輸出'].map((item, index) => <li key={item}><span className="font-mono">0{index + 1}</span>{item}</li>)}
                </ul>
                <p className="text-body-lg mt-loose">KMKRは、使う現場の困りごとに目を向け、改善につながる商品を発掘し、日本の事業者様へ提案する貿易会社です。中国での調達・OEM対応から輸出まで、ご相談に応じて進めます。</p>
              </div>
            </div>
          </div>
        </section>

        <section id="flow" className="home-flow">
          <div className="layout-safe"><SectionLabel number="03" label="FLOW / お取引の流れ" /><h2 className="text-h1 font-black tracking-tight">お取引の流れ</h2><ol className="flow-list reveal">{data.flow.map((item, index) => <li key={item}><span className="font-mono">0{index + 1}</span><p>{item}</p></li>)}</ol></div>
        </section>

        <section id="message" className="founder-section">
          <BlueprintTexture variant="compact" />
          <div className="layout-safe">
            <SectionLabel number="04" label="MESSAGE / 代表メッセージ" />
            <div className="founder-grid reveal">
              <figure className="founder-figure">
                <img src={founderPhoto} alt="創業者・代表 王家傲" />
                <figcaption><span className="font-mono">FOUNDER / CEO</span><strong>王 家傲</strong></figcaption>
              </figure>
              <div className="founder-copy">
                <p className="font-mono text-caption text-brand-primary">MESSAGE FROM THE FOUNDER</p>
                <h2 className="text-h1 font-black tracking-tight">代表メッセージ</h2>
                <p className="founder-lead mt-loose text-h3 font-bold leading-relaxed">お客さまに喜んでもらいたい。<br />その思いを、私たちKMKRがお支えいたします。</p>
                <div className="founder-prose">
                  <p>日本に留学していた頃、よく利用していたお弁当屋さんがありました。おいしく、手頃な値段で、お弁当にはいつも手書きのメッセージが添えられていました。その心遣いに、何度も温かい気持ちになりました。コロナ禍で閉店することになったときも、最後のお知らせには、お客さまへの感謝が丁寧につづられていました。今も心に残っている出来事です。</p>
                  <p>おいしいお弁当と、添えられた手書きの言葉。その両方から、「お客さまに喜んでもらいたい」というお店の思いを感じていました。今度は私が、貿易を通じて、そうした思いを持つ企業の力になりたいと考えています。</p>
                  <p>多くの人に知られていなくても、誰かにとって大切な商品がある。その商品を届けるために、日々工夫を重ねる人がいる。売上の大きさだけで判断せず、その商品を必要とする人と、届ける人の思いにも目を向けていきたい。それが、私たちの大切にしたい姿勢です。</p>
                  <p>KMKRは、中国での商品発掘・調達、OEM対応から日本への輸出までを通じて、日本の事業者の皆さまの商品づくりと仕入れを支援します。どのような商品が必要なのか、その商品を通じてお客さまに何を届けたいのか。思いと条件の両方を伺い、用途や品質、価格、数量などの条件に合う商品や調達先を共に探していきます。</p>
                  <p>皆さまが大切にしているお客さまを、私たちも大切に思いながら、一つひとつの仕事に向き合う。その積み重ねを通じて、信頼を育てていきます。</p>
                </div>
                <div className="founder-profile"><h3 className="text-h3 font-bold">創業者略歴</h3><p>横浜国立大学経営学部卒業。中国の貿易会社で、日本向けの商品提案・見積対応・仕入先との調整に携わる。2026年8月、加末久良（宁波）国际贸易有限公司を設立。</p></div>
              </div>
            </div>
          </div>
        </section>

        <section id="company" className="home-company">
          <div className="layout-safe">
            <SectionLabel number="05" label="COMPANY / 会社概要" />
            <div className="company-grid reveal">
              <h2 className="text-h1 font-black tracking-tight">企業概要</h2>
              <dl>{[['会社名', data.company.name], ['英文表記', data.company.english], ['設立', data.company.established], ['代表者', data.company.representative], ['所在地', data.company.address], ['事業内容', data.company.business], ['お問い合わせ', data.company.email]].map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}</dl>
            </div>
          </div>
        </section>

        <section id="inquiry" className="home-contact" ref={transportEnd}>
          <div className="layout-safe home-contact-grid"><div><SectionLabel number="06" label="CONTACT / お問い合わせ" /><h2 className="text-h1 font-black tracking-tight">お問い合わせ</h2><p>商品やお取引、OEM・調達に関するご相談など、お気軽にお問い合わせください。</p></div><button className="contact-link" onClick={() => go('contact')}>お問い合わせへ進む<ArrowUpRight size={28} /></button></div>
        </section>
      </main>
    </Shell>
  );
}
